import throttle from 'lodash.throttle';
import React, { useContext, useEffect, useRef, useState } from 'react';
import LazyLoad from 'react-lazyload';

import Link from '../../components/link/link';
import LayoutContext from '../../contexts/layoutContext';
import {
  Drupal_ParagraphMarketoForm,
  Drupal_ParagraphStickySlider
} from '../../generated/graphql';
import { getProcessedValue } from '../../utils/helpers';
import './sticky-slider.scss';
import useMarketoLink from '../../hooks/use-marketo-link';

interface StickySliderProps {
  sections: Drupal_ParagraphStickySlider[];
  mktoFormEntity: Drupal_ParagraphMarketoForm | null;
  buttonText: string;
}

const StickySlider = ({
  sections,
  mktoFormEntity,
  buttonText
}: StickySliderProps) => {
  const isBrowser = typeof window !== 'undefined';
  const slideHeight = 900;
  const sliderRef = useRef<HTMLDivElement>(null);

  const positionStickySupport = () => {
    let support = true;
    if (isBrowser) {
      const el = document.createElement('a');
      const mStyle = el.style;
      mStyle.cssText =
        'position:sticky;position:-webkit-sticky;position:-ms-sticky;';
      if (!mStyle.position.includes('sticky')) {
        support = false;
      }
    }
    return support;
  };

  const [slideIndex, setSlideIndex] = useState(0);
  const stickyIsSupported = positionStickySupport();

  const onScroll = throttle(() => {
    const sliderElement = sliderRef.current;
    if (sliderElement) {
      const sliderPosition = sliderElement.getBoundingClientRect();
      if (sliderPosition.top < 0) {
        const currentSlide = Math.round(
          Math.abs(sliderPosition.top / slideHeight)
        );
        if (currentSlide <= sections.length - 1) {
          setSlideIndex(currentSlide);
        }
      } else {
        setSlideIndex(0);
      }
    }
  }, 250);

  useEffect(() => {
    const sliderElement = sliderRef.current;
    if (stickyIsSupported && sliderElement) {
      sliderElement.style.minHeight = `${sections.length * slideHeight}px`;
      window.addEventListener('scroll', onScroll);
    }
  }, [onScroll, sections.length, stickyIsSupported]);

  const scrollToSlide = (i: number) => {
    if (window.innerWidth > 1219) {
      const sliderElement = sliderRef.current;
      if (sliderElement) {
        const parent = sliderElement.parentElement;
        if (parent) {
          const scrollDistance = slideHeight * i + parent.offsetTop;
          window.scrollTo(0, scrollDistance);
        }
      }
    }
  };

  const { language } = useContext(LayoutContext);
  const getMarketoLink = useMarketoLink(language);

  const { url, copy } = getMarketoLink(mktoFormEntity?.fieldMarketoId);

  return (
    <section className="sticky-slider-component lucid-container">
      <div className="sticky-slider relative" ref={sliderRef}>
        <div
          className={`sticky-slider__image-wrapper ${
            stickyIsSupported ? 'hide-tablet' : 'hide'
          }`}
        >
          <div className="sticky-slider__image-sticky">
            {sections.map(
              (
                {
                  fieldHeader,
                  fieldHideImageShadow,
                  fieldDoNotAddBrow,
                  fieldImage
                },
                i
              ) => {
                return (
                  <div
                    key={fieldHeader!}
                    className={`sticky-slider__image card ${
                      fieldHideImageShadow ? '' : 'card--raised'
                    } ${slideIndex === i ? 'sticky-slider__image--show' : ''}`}
                  >
                    {!fieldDoNotAddBrow && (
                      <img
                        alt="browserbar"
                        src="https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/consideration/basic-images/browser-bar@2x.png"
                      />
                    )}
                    <LazyLoad offset={10}>
                      <img src={fieldImage!} alt={fieldImage!} />
                    </LazyLoad>
                  </div>
                );
              }
            )}
            {mktoFormEntity && (
              <div className="layout  layout--reversed  margin-t-md">
                <div className="layout__item  layout__item--5">
                  <Link to={url} asButton primary block>
                    {buttonText || copy}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="sticky-slider__sidebar">
          {sections.map(
            ({ fieldHeader, fieldImage, fieldBody, fieldCta }, i) => (
              <div key={fieldHeader!}>
                <div
                  className={`layout ${
                    stickyIsSupported ? 'hide-tablet' : 'hide'
                  }`}
                >
                  <div className="layout__item  layout__item--5  border-t-light" />
                </div>
                <div className="layout  margin-b-lg-tablet border-t-light">
                  <div
                    className={`layout__item  layout__item--12-tablet card  card--raised  card--light  margin-b-md-tablet ${
                      stickyIsSupported ? 'hide-desktop' : ''
                    }`}
                  >
                    <img
                      className="sticky-slider__sidebar-image"
                      src="https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/consideration/basic-images/browser-bar@2x.png"
                      alt="browserbar"
                    />
                    <img
                      className="sticky-slider__sidebar-image"
                      src={fieldImage!}
                      alt={fieldHeader!}
                    />
                  </div>
                  <section
                    className={`layout__item layout__item--3 layout__item--9-tablet layout__item--12-mobile sticky-slider__text ${
                      stickyIsSupported && slideIndex === i
                        ? 'sticky-slider__text--open'
                        : ''
                    } ${
                      !stickyIsSupported
                        ? 'sticky-slider__text--open layout__item--9'
                        : ''
                    }`}
                  >
                    <button
                      className="sticky-slider__button"
                      onClick={() => scrollToSlide(i)}
                    >
                      <h3 className="sticky-slider__header  js-sticky-slider-toggle">
                        {fieldHeader}
                      </h3>
                    </button>
                    {fieldBody && (
                      <div className="sticky-slider__contents">
                        <div
                          className="card__description"
                          dangerouslySetInnerHTML={{
                            __html: getProcessedValue(fieldBody)
                          }}
                        />
                        {fieldCta && fieldCta.url && (
                          <a
                            href={fieldCta.url.path!}
                            className="link link--primary"
                          >
                            {fieldCta.title}
                          </a>
                        )}
                      </div>
                    )}
                  </section>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </section>
  );
};

export default StickySlider;
